import { FC } from 'react';
import { Tile, Link, Button } from '@carbon/react';
import { Edit, DirectionMerge } from '@carbon/react/icons';
import { AddressReviewFlag } from './address-review-flag';
import { EntityClassification, AliasesList } from './reconcile/alias-fields';
import type { AliasClusterInfo } from './reconcile/use-alias-pill-states';
import type {
  CurrentAnchor,
  EntityAddress,
  StructuredPartyDetail,
} from './types';
import { anchorName, anchorSourceLabel } from './utils';
import { partyRowLock } from './party-status';
import { formatConfidence } from 'utils/number';
import style from './staged-entity-card.module.scss';

interface Props {
  party: StructuredPartyDetail;
  onEditClick?: () => void;
  onCanonizeClick?: () => void;
  // Disable Canonize when review flags are raised on the entity (per the
  // narrative) — the caller also passes true once a match is confirmed.
  canonizeDisabled?: boolean;
  // Alias-cluster info for the staged entity (role + peer names), computed by
  // the parent across the visible match set.
  aliasInfo?: AliasClusterInfo;
  // The Imported entity an In Progress party was matched into (the Deduplicated
  // Result). Rendered in place of the staged entity, with the confidence of the
  // match that brought the party there.
  anchor?: CurrentAnchor | null;
}

export const StagedEntityCard: FC<Props> = ({
  party,
  onEditClick,
  onCanonizeClick,
  canonizeDisabled = false,
  aliasInfo,
  anchor,
}) => {
  const staged = anchor ?? party.stagedEntity;
  const displayName = (anchor ? anchorName(party) : party.name) || '—';
  const addresses: Array<EntityAddress & { primary?: boolean }> =
    staged?.addresses && staged.addresses.length > 0
      ? staged.addresses
      : staged?.primaryAddress
        ? [{ ...staged.primaryAddress, primary: true }]
        : [];
  // Edits land on the current anchor, so an In Progress party stays editable,
  // as its parties-overview row does; a complete, unresolvable or removed one
  // does not.
  const editLocked = partyRowLock(party).locked || party.status === 'removed';
  const editable = staged && !editLocked && onEditClick;

  return (
    <Tile className={style.card}>
      <div className={style.header}>
        <span className={style.eyebrow}>
          {anchorSourceLabel(anchor?.source)}
        </span>
        <div className={style.headerActions}>
          {staged && onCanonizeClick && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={DirectionMerge}
              iconDescription="Add as New"
              tooltipPosition="left"
              onClick={onCanonizeClick}
              disabled={canonizeDisabled}
              className={style.canonizeButton}
            />
          )}
          {editable && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Edit}
              iconDescription="Edit Entity Details"
              tooltipPosition="left"
              onClick={onEditClick}
              className={style.editButton}
            />
          )}
        </div>
      </div>
      <h5 className={style.name}>
        {/* Always link the staged entity, even once a match is confirmed/merged:
            its wrapper is soft-deleted then, but /entities/:id now renders the
            deduplicated entity read-only instead of 404ing. */}
        {staged ? (
          <Link href={`/entities/${staged.id}`}>{displayName}</Link>
        ) : (
          displayName
        )}
      </h5>
      <dl className={style.fields}>
        <div className={style.field}>
          <dt>Role</dt>
          <dd>{party.role || '—'}</dd>
        </div>
        <div className={style.field}>
          <dt>Entity Type</dt>
          <dd>Imported</dd>
        </div>
        <div className={style.field}>
          <dt>Entity ID</dt>
          <dd>{staged ? staged.id : '—'}</dd>
        </div>
        <div className={style.field}>
          <dt>Entity Classification</dt>
          <dd>
            {staged ? (
              <EntityClassification info={aliasInfo} entityId={staged.id} />
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div className={style.field}>
          <dt>Aliases</dt>
          <dd>
            {staged ? (
              <AliasesList
                aliases={aliasInfo?.aliases ?? []}
                entityId={staged.id}
              />
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div className={style.field}>
          <dt>{addresses.length > 1 ? 'Addresses' : 'Address'}</dt>
          <dd>
            {addresses.length === 0
              ? '—'
              : addresses.map((address, i) => (
                  <div key={address.id} className={style.addressRow}>
                    <span className={style.addressText}>
                      {addresses.length > 1 && (
                        <span className={style.addressLabel}>
                          {address.primary ? 'Primary:' : `${i + 1}.`}{' '}
                        </span>
                      )}
                      {address.fullText || address.asFoundAddress || '—'}
                    </span>
                    {address.reviewFlag && (
                      <AddressReviewFlag flag={address.reviewFlag} />
                    )}
                  </div>
                ))}
          </dd>
        </div>
        <div className={style.field}>
          <dt>Phone</dt>
          <dd>{staged?.phoneNumber || '—'}</dd>
        </div>
        {anchor && (
          <div className={style.field}>
            <dt>Confidence</dt>
            <dd>{formatConfidence(party.arrivalConfidence)}</dd>
          </div>
        )}
      </dl>
    </Tile>
  );
};
